import { Routes } from '@angular/router';
import { AppLayoutComponent } from './app-layout/app-layout.component';
import { EditContractComponent } from './contract-management/components/edit-contract/edit-contract.component';
import { AuthGuard } from './@core/guards/auth.guard';
import { LogInComponent } from './auth/login/login.component';
import { PageNotFoundComponent } from './auth/page-not-found/page-not-found.component';
import { SignUpComponent } from './auth/sign-up/sign-up.component';
import { OnboardOrgComponent } from './auth/sign-up/components/onboard-org/onboard-org.component';
import { DocGuard } from './@core/guards/doc.guard';
import { EmailAuthenticationComponent } from './contract-management/components/email-authentication/email-authentication.component';
import { LoggedInGuard } from './@core/guards/logged-in.guard';
import { ForgotPasswordComponent } from './auth/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { VersionsComponent } from './contract-management/components/versions/versions.component';
import { SubscriptionGuard } from './@core/guards/subscription.guard';
import { CompareReportViewComponent } from './contract-management/components/edit-contract/components/compare/compare-report-view/compare-report-view.component';
import { PricingComponent } from './pricing/pricing.component';
import { EXPLORE_ROUTES } from './features/explore/explore.routes';
import { GEOTECHNICAL_ANALYSIS_LAYOUT_ROUTES, GEOTECHNICAL_ANALYSIS_TOP_ROUTES } from './features/geotechnical-analysis/geotechnical-analysis.routes';
import { ProjectsComponent } from './contract-management/components/projects/projects.component';
import { DocumentsComponent } from './contract-management/components/documents/documents.component';
import { ProjectDashboardComponent } from './contract-management/components/project-dashboard/project-dashboard.component';
import { ProfileComponentSettings } from './contract-management/components/settings/profile/profile.component';
import { ConsumptionDetailsComponent } from './contract-management/components/settings/consumption-details/consumption-details.component';
import { PermissionGuard } from './@core/guards/permission.guard';
import { USER_ROLE } from './@core/models/enums';
import { MicrosoftOauthCallbackComponent } from './contract-management/components/microsoft-oauth-callback/microsoft-oauth-callback.component';
import { VerifyComponent } from './auth/sign-up/components/verify/verify.component';
import { ProfileComponent } from './auth/profile/profile.component';
import { SETTINGS_ROUTES } from './contract-management/components/settings/settings.routes';
import { GENERAL_AGENT_ROUTES } from './features/general-agent/general-agent.routes';
import { ThreadShareGuard } from './@core/guards/thread-share.guard';
import { THREAD_AGENT_SERVICE } from './features/general-agent/tokens/thread-agent.token';
import { THREAD_AGENT_CONFIG, GENERAL_AGENT_THREAD_CONFIG } from './features/general-agent/tokens/thread-agent-config.token';
import { GeneralAgentService } from './features/general-agent/services/general-agent.service';
import { ProjectKnowledgeComponent } from './contract-management/components/project-knowledge/project-knowledge.component';
import { GenerateWithAiComponent } from './contract-management/components/explore/generate-with-ai/generate-with-ai.component';
import { ExploreComponent } from './features/explore/pages/explore/explore.component';


export const routes: Routes = [
    {
        path: '',
        component: AppLayoutComponent,
        canActivate: [AuthGuard, SubscriptionGuard],
        children: [
            { path: '', redirectTo: 'explore', pathMatch: 'full' },
            {
                path: 'explore',
                children: [
                    {
                        path: '',
                        component: ExploreComponent,
                        pathMatch: 'full',
                        data: { breadcrumb: 'Explore' },
                    },
                    {
                        path: 'callback/:connectorToolkit',
                        component: ExploreComponent,
                        canActivate: [AuthGuard],
                    }
                ]
            },
            {
                path: 'generate-with-ai',
                component: GenerateWithAiComponent,
                data: { breadcrumb: 'Contract generator' }
            },
            ...GEOTECHNICAL_ANALYSIS_LAYOUT_ROUTES,
            {
                path: 'bob',
                children: GENERAL_AGENT_ROUTES
            },
            {
                path: 'projects',
                component: ProjectsComponent,
                data: { breadcrumb: 'Projects' },
                children: [
                    {
                        path: 'dashboard',
                        component: ProjectDashboardComponent,
                        canActivate: [AuthGuard],
                        data: { breadcrumb: 'Project dashboard' },
                    },
                    {
                        path: 'project-knowledge',
                        component: ProjectKnowledgeComponent,
                        canActivate: [AuthGuard],
                        data: { breadcrumb: 'Knowledge' }
                    }
                ]

            },
            {
                path: 'documents',
                component: DocumentsComponent,
                data: { breadcrumb: 'Documents' }
            },
            {
                path: 'profile',
                component: ProfileComponentSettings,
                data: { breadcrumb: 'Profile' }
            },
            {
                path: 'pricing',
                component: PricingComponent,
                canActivate: [SubscriptionGuard, PermissionGuard],
                data: { breadcrumb: 'Pricing', roles: [USER_ROLE.SUPER_ADMIN], allowBillingAccess: true }
            },
            {
                path: 'settings',
                children: SETTINGS_ROUTES,
            },
            {
                path: 'consumption',
                component: ConsumptionDetailsComponent,
                data: { breadcrumb: 'Consumption' }
            }
        ]
    },
    {
        path: 'login',
        canActivate: [LoggedInGuard],
        component: LogInComponent
    },
    {
        path: 'login/oauth/:provider/callback',
        canActivate: [LoggedInGuard],
        component: LogInComponent
    },
    {
        path: 'oauth/microsoft/organization/callback',
        component: MicrosoftOauthCallbackComponent
    },
    {
        path: 'verify/:email',
        component: VerifyComponent
    },
    {
        path: 'signup',
        component: SignUpComponent
    },
    {
        path: 'forgot-password',
        component: ForgotPasswordComponent
    },
    {
        path: 'reset-password',
        component: ResetPasswordComponent
    },
    {
        path: 'onboard/:email',
        canActivate: [LoggedInGuard],
        component: OnboardOrgComponent
    },
    {
        path: 'invitation-signup',
        component: ProfileComponent,
    },
    {
        path: 'onboard',
        component: OnboardOrgComponent
    },
    {
        path: 'edit-document',
        component: EditContractComponent,
        canActivate: [DocGuard, SubscriptionGuard],
        children: [
            {
                path: 'share',
                canActivate: [DocGuard],
                component: EditContractComponent
            }
        ]
    },
    {
        // Public "Anyone with the link" entry point for a Bob thread — outside the
        // AuthGuard-protected app shell so anonymous visitors aren't sent to /login.
        path: 'shared-thread',
        loadComponent: () => import('./features/general-agent/pages/public-thread-view/public-thread-view.component')
            .then(m => m.PublicThreadViewComponent),
        canActivate: [ThreadShareGuard],
        providers: [
            { provide: THREAD_AGENT_SERVICE, useExisting: GeneralAgentService },
            { provide: THREAD_AGENT_CONFIG, useValue: GENERAL_AGENT_THREAD_CONFIG },
        ],
    },
    ...GEOTECHNICAL_ANALYSIS_TOP_ROUTES,
    {
        path: 'versions',
        canActivate: [SubscriptionGuard],
        component: VersionsComponent
    },
    {
        path: 'compare',
        canActivate: [SubscriptionGuard],
        component: CompareReportViewComponent,
        data: { breadcrumb: 'Compare' }
    },
    {
        path: 'email-auth',
        component: EmailAuthenticationComponent
    },
    {
        path: '**',
        component: PageNotFoundComponent
    },
];
